/**
 * ===================================================================
 * REACT HOOK - GROWING AREAS
 * ===================================================================
 * Hook pro spravu pestebnich oblasti s React Query.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { growingAreaService, RecommendationQueryParams } from '../services/growingAreaService';
import type { CreateGrowingAreaDTO, UpdateGrowingAreaDTO } from '../types/growing-areas';

// Query keys
const QUERY_KEYS = {
  areas: ['growing-areas'] as const,
  deviceAreas: (deviceUuid: string) => ['growing-areas', 'device', deviceUuid] as const,
  area: (areaId: string) => ['growing-areas', areaId] as const,
  slots: (areaId: string) => ['growing-areas', areaId, 'slots'] as const,
  occupancy: (areaId: string) => ['growing-areas', areaId, 'occupancy'] as const,
  recommendations: (areaId: string) => ['growing-areas', areaId, 'recommendations'] as const,
};

/**
 * Hook pro ziskani vsech oblasti zarizeni
 */
export const useDeviceGrowingAreas = (deviceUuid: string | undefined) => {
  return useQuery({
    queryKey: deviceUuid ? QUERY_KEYS.deviceAreas(deviceUuid) : QUERY_KEYS.areas,
    queryFn: () =>
      deviceUuid ? growingAreaService.getDeviceAreas(deviceUuid) : growingAreaService.getAllAreas(),
    enabled: true, // Always enabled, will fetch either specific or all
    staleTime: 30000,
  });
};

/**
 * Hook pro ziskani jedne oblasti
 */
export const useGrowingArea = (areaId: string | undefined) => {
  return useQuery({
    queryKey: QUERY_KEYS.area(areaId || ''),
    queryFn: () => growingAreaService.getArea(areaId!),
    enabled: !!areaId,
    staleTime: 30000,
  });
};

/**
 * Hook pro ziskani slotu oblasti
 */
export const useAreaSlots = (areaId: string | undefined) => {
  return useQuery({
    queryKey: QUERY_KEYS.slots(areaId || ''),
    queryFn: () => growingAreaService.getSlots(areaId!),
    enabled: !!areaId,
    staleTime: 30000,
  });
};

/**
 * Hook pro ziskani obsazenosti oblasti
 */
export const useGrowingAreaOccupancy = (areaId: string | undefined) => {
  return useQuery({
    queryKey: QUERY_KEYS.occupancy(areaId || ''),
    queryFn: () => growingAreaService.getOccupancy(areaId!),
    enabled: !!areaId,
    staleTime: 30000,
    retry: (failureCount, error: any) => {
      // Don't retry on 404 (area doesn't exist)
      if (error?.response?.status === 404) return false;
      return failureCount < 2;
    },
  });
};

/**
 * Hook pro ziskani doporuceni rostlin pro oblast
 */
export const useAreaRecommendations = (
  areaId: string | undefined,
  params?: RecommendationQueryParams,
) => {
  return useQuery({
    queryKey: [...QUERY_KEYS.recommendations(areaId || ''), params],
    queryFn: () => growingAreaService.getRecommendations(areaId!, params),
    enabled: !!areaId,
    staleTime: 60000, // Doporuceni se meni mene casto
    retry: (failureCount, error: any) => {
      if (error?.response?.status === 404 || error?.response?.status === 500) return false;
      return failureCount < 2;
    },
  });
};

/**
 * Hook pro vytvoreni nove oblasti
 */
export const useCreateGrowingArea = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateGrowingAreaDTO) => growingAreaService.createArea(data),
    onSuccess: (newArea) => {
      // Invaliduj cache pro specifické zařízení (pokud existuje)
      if (newArea.device_uuid) {
        queryClient.invalidateQueries({
          queryKey: QUERY_KEYS.deviceAreas(newArea.device_uuid),
        });
      }

      // Invaliduj cache pro "bez zařízení" / manuální (prázdný řetězec)
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.deviceAreas(''),
      });

      // Invaliduj obecný seznam oblastí (pokud se používá jinde)
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.areas,
      });
    },
  });
};

/**
 * Hook pro aktualizaci oblasti
 */
export const useUpdateGrowingArea = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ areaId, data }: { areaId: string; data: UpdateGrowingAreaDTO }) =>
      growingAreaService.updateArea(areaId, data),
    onSuccess: (updatedArea) => {
      // Aktualizuj cache pro tuto oblast
      queryClient.setQueryData(QUERY_KEYS.area(updatedArea.id), updatedArea);
      // Invaliduj seznam oblasti
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.deviceAreas(updatedArea.device_uuid || ''),
      });
    },
  });
};

/**
 * Hook pro smazani oblasti
 */
export const useDeleteGrowingArea = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (areaId: string) => growingAreaService.deleteArea(areaId),
    onSuccess: () => {
      // Invaliduj vsechny growing-areas queries
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.areas });
    },
  });
};

/**
 * Hook pro prirazeni rostliny do slotu
 */
export const useAssignPlantToSlot = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      slotId,
      plantId,
      isAnchor,
      batchId,
      data,
    }: {
      slotId: string;
      plantId?: string;
      isAnchor?: boolean;
      batchId?: string;
      data?: any;
    }) => growingAreaService.assignPlantToSlot(slotId, plantId, isAnchor, batchId, data),
    onSuccess: () => {
      // Invaliduj slots a occupancy
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.areas });
    },
  });
};

/**
 * Hook pro odstraneni rostliny ze slotu
 */
export const useRemovePlantFromSlot = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ slotId, plantId }: { slotId: string; plantId: string }) =>
      growingAreaService.removePlantFromSlot(slotId, plantId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.areas });
    },
  });
};

/**
 * Hook pro presun rostliny mezi sloty
 */
export const useMovePlant = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      plantId,
      fromSlotId,
      toSlotId,
    }: {
      plantId: string;
      fromSlotId: string;
      toSlotId: string;
    }) => growingAreaService.movePlant(plantId, fromSlotId, toSlotId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.areas });
    },
  });
};

/**
 * Hook pro vytvoreni slot eventu
 */
export const useCreateSlotEvent = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ slotId, eventType, data }: { slotId: string; eventType: string; data?: any }) =>
      growingAreaService.createSlotEvent(slotId, eventType, data),
    onSuccess: () => {
      // Invalidujeme data oblasti, aby se projevily zmeny v historii
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.areas });
    },
  });
};

/**
 * Kombinovany hook pro kompletni spravu oblasti
 */
export const useGrowingAreas = (deviceUuid: string | undefined) => {
  const areasQuery = useDeviceGrowingAreas(deviceUuid);
  const createMutation = useCreateGrowingArea();
  const updateMutation = useUpdateGrowingArea();
  const deleteMutation = useDeleteGrowingArea();

  return {
    // Data
    areas: areasQuery.data || [],
    isLoading: areasQuery.isLoading,
    error: areasQuery.error,

    // Actions
    refetch: areasQuery.refetch,
    createArea: createMutation.mutateAsync,
    updateArea: updateMutation.mutateAsync,
    deleteArea: deleteMutation.mutateAsync,

    // Mutation states
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,
  };
};
